import {type ChangeEvent} from 'react'
import {useTranslation} from 'react-i18next'
import type {DotType} from 'qr-code-styling'
import {QR_DOT_TYPE_OPTIONS, QR_GRADIENT_ANGLE_RANGE} from '../../../common/constants/qrStyleOptions'
import type {QrStyleState} from '../../../common/utils/qrRenderer'
import {trackEvent} from '../../../common/analytics/trackEvent'
import QrGradientBar from './QrGradientBar'

interface QrStylePanelProps {
  style: QrStyleState
  onChange: (patch: Partial<QrStyleState>) => void
}

const QrStylePanel = ({style, onChange}: QrStylePanelProps) => {
  const {t} = useTranslation()

  const setMode = (colorMode: QrStyleState['colorMode']) => {
    onChange({colorMode})
    trackEvent('qr_style_changed', {field: 'colorMode', value: colorMode})
  }

  return (
    <div className="qrg-style">
      <label className="qrg-field">
        <span className="qrg-label">{t('qrGenerator.style.pattern')}</span>
        <select
          className="qrg-select"
          value={style.dotType}
          onChange={(e: ChangeEvent<HTMLSelectElement>) => {
            onChange({dotType: e.target.value as DotType})
            trackEvent('qr_style_changed', {field: 'dotType', value: e.target.value})
          }}
        >
          {QR_DOT_TYPE_OPTIONS.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {t(`qrGenerator.style.patterns.${opt.labelKey}`)}
            </option>
          ))}
        </select>
      </label>

      <div className="qrg-tabs">
        <button
          type="button"
          className={`qrg-tab${style.colorMode === 'solid' ? ' is-active' : ''}`}
          onClick={() => setMode('solid')}
        >
          {t('qrGenerator.style.solid')}
        </button>
        <button
          type="button"
          className={`qrg-tab${style.colorMode === 'gradient' ? ' is-active' : ''}`}
          onClick={() => setMode('gradient')}
        >
          {t('qrGenerator.style.gradient')}
        </button>
      </div>

      {style.colorMode === 'solid' && (
        <div className="qrg-row">
          <label className="qrg-label">{t('qrGenerator.style.color')}</label>
          <input
            type="color"
            className="qrg-color-input"
            value={style.color}
            onChange={(e) => onChange({color: e.target.value})}
          />
        </div>
      )}

      {style.colorMode === 'gradient' && (
        <>
          <QrGradientBar stops={style.gradient} onChange={(gradient) => onChange({gradient})} />
          <label className="qrg-slider">
            <span className="qrg-slider-label">{t('qrGenerator.style.angle')}</span>
            <input
              type="range"
              min={QR_GRADIENT_ANGLE_RANGE.min}
              max={QR_GRADIENT_ANGLE_RANGE.max}
              step={QR_GRADIENT_ANGLE_RANGE.step}
              value={style.gradientAngle}
              onChange={(e) => onChange({gradientAngle: parseInt(e.target.value, 10)})}
            />
            <span className="qrg-slider-value">{style.gradientAngle}°</span>
          </label>
        </>
      )}

      <label className="qrg-toggle">
        <input
          type="checkbox"
          checked={style.bgTransparent}
          onChange={(e) => onChange({bgTransparent: e.target.checked})}
        />
        {t('qrGenerator.style.transparentBg')}
      </label>

      {!style.bgTransparent && (
        <div className="qrg-row">
          <label className="qrg-label">{t('qrGenerator.style.bgColor')}</label>
          <input
            type="color"
            className="qrg-color-input"
            value={style.bgColor}
            onChange={(e) => onChange({bgColor: e.target.value})}
          />
        </div>
      )}
    </div>
  )
}

export default QrStylePanel
